import React, { Component } from 'react';
import { types, typeHelpers } from '@polymathnetwork/new-shared';
import { SvgPaperplane } from '~/images/icons/Paperplane';
import { Icon } from '~/components/Icon';
import { Button } from '~/components/Button';
import { Paragraph } from '~/components/Paragraph';
import { Flex } from '~/components/Flex';
import { Box } from '~/components/Box';
import { Modal, ModalStatus } from '~/components/Modal';
import { TransactionItem } from './TransactionItem';
import { getTransactionQueueTitle } from '~/components/utils/contentMappings';

type ModalProps = typeHelpers.Omit<
  typeHelpers.GetProps<typeof Modal>,
  'status'
>;

const { TransactionQueueStatus } = types;

const getModalStatus = (status: types.TransactionQueueStatus) =>
  ({
    [TransactionQueueStatus.Idle]: ModalStatus.Loading,
    [TransactionQueueStatus.Running]: ModalStatus.Loading,
    [TransactionQueueStatus.Succeeded]: ModalStatus.Success,
    [TransactionQueueStatus.Failed]: ModalStatus.Alert,
  }[status]);

const getLabelText = (status: ModalStatus) =>
  ({
    [ModalStatus.Loading]: 'Processing',
    [ModalStatus.Idle]: 'Processing',
    [ModalStatus.Warning]: 'Failed',
    [ModalStatus.Alert]: 'Failed',
    [ModalStatus.Success]: 'Completed',
  }[status]);

export interface ModalTransactionQueueProps extends ModalProps {
  transactionQueue: types.TransactionQueuePojo;
  onContinue: () => void;
  withEmail?: boolean;
  continueButtonText?: string;
  getTitle: (queue: types.TransactionQueuePojo) => string;
  getTransactionTitle?: (transaction: types.TransactionPojo) => string;
  transactionLinkSubdomain?: string;
}

export class ModalTransactionQueue extends Component<
  ModalTransactionQueueProps
> {
  public static defaultProps = {
    continueButtonText: 'Continue',
    getTitle: getTransactionQueueTitle,
  };

  public render() {
    const {
      transactionQueue,
      withEmail,
      isOpen,
      continueButtonText,
      onContinue,
      getTitle,
      getTransactionTitle,
      transactionLinkSubdomain,
    } = this.props;

    const { transactions, status } = transactionQueue;
    const modalStatus = getModalStatus(status);
    const isSuccess = status === TransactionQueueStatus.Succeeded;
    const isRejected = status === TransactionQueueStatus.Failed;

    return (
      <Modal
        isOpen={isOpen}
        isCloseable={false}
        status={modalStatus}
        maxWidth={500}
        isCentered={false}
      >
        <Modal.Header
          status={modalStatus}
          label={'Transaction ' + getLabelText(modalStatus)}
        >
          {getTitle(transactionQueue)}
        </Modal.Header>

        {transactions.map((transaction, _index, allTransactions) => (
          <TransactionItem
            key={transaction.uid}
            transaction={transaction}
            getTitle={getTransactionTitle}
            allTransactions={allTransactions}
            transactionLinkSubdomain={transactionLinkSubdomain}
          />
        ))}

        {isSuccess && withEmail && (
          <Flex mt="auto" pt="gridGap">
            <Box minWidth={50} mt={1}>
              <Icon Asset={SvgPaperplane} width="30" height="30" />
            </Box>
            <Paragraph fontSize={2}>
              We just sent you an email with the transaction details for your
              records. Check your inbox.
            </Paragraph>
          </Flex>
        )}

        {(isSuccess || isRejected) && (
          <Paragraph textAlign="center" mt="auto" pt="gridGap">
            <Button onClick={onContinue}>{continueButtonText}</Button>
          </Paragraph>
        )}
      </Modal>
    );
  }
}
try {
    // @ts-ignore
    ModalTransactionQueue.displayName = "ModalTransactionQueue";
    // @ts-ignore
    ModalTransactionQueue.__docgenInfo = { "description": "", "displayName": "ModalTransactionQueue", "props": { "transactionQueue": { "defaultValue": null, "description": "", "name": "transactionQueue", "required": true, "type": { "name": "TransactionQueuePojo" } }, "onContinue": { "defaultValue": null, "description": "", "name": "onContinue", "required": true, "type": { "name": "() => void" } }, "withEmail": { "defaultValue": null, "description": "", "name": "withEmail", "required": false, "type": { "name": "boolean" } }, "continueButtonText": { "defaultValue": { value: "Continue" }, "description": "", "name": "continueButtonText", "required": false, "type": { "name": "string" } }, "getTitle": { "defaultValue": null, "description": "", "name": "getTitle", "required": true, "type": { "name": "(queue: TransactionQueuePojo) => string" } }, "getTransactionTitle": { "defaultValue": null, "description": "", "name": "getTransactionTitle", "required": false, "type": { "name": "(transaction: TransactionPojo) => string" } }, "transactionLinkSubdomain": { "defaultValue": null, "description": "", "name": "transactionLinkSubdomain", "required": false, "type": { "name": "string" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/ModalTransactionQueue/ModalTransactionQueue.tsx#ModalTransactionQueue"] = { docgenInfo: ModalTransactionQueue.__docgenInfo, name: "ModalTransactionQueue", path: "src/components/ModalTransactionQueue/ModalTransactionQueue.tsx#ModalTransactionQueue" };
}
catch (__react_docgen_typescript_loader_error) { }